'use client'

import NumberFlow from '@number-flow/react'
import { InfoIcon, ShootingStarIcon } from '@phosphor-icons/react'
import * as CheckboxPrimitive from '@radix-ui/react-checkbox'
import { PopoverPortal } from '@radix-ui/react-popover'
import clsx from 'clsx'
import { useRouter } from 'next/router'
import { useEffect, useRef, useState } from 'react'
import CantonCombobox from '@/blocks/PriceCalculator/Combobox'
import {
  ASSESSMENT_NEUTRAL,
  buildSteps,
  ADMIN_FEE,
  computeResult,
  formatChf,
  LESSON_SLIDER_MAX,
  LESSON_SLIDER_MIN,
  lessonBreakdownRows,
  LICENSE_COMPLETED_STEPS,
  packLessons,
  recommendLessons,
  RESULT_ROWS,
  Row,
  Step,
  StepId,
} from '@/blocks/PriceCalculator/logic'
import {
  CantonCode,
  CANTONS,
  CantonPrices,
  getPrices,
  PricesResponse,
} from '@/blocks/PriceCalculator/prices'
import RangeSlider from '@/blocks/PriceCalculator/RangeSlider'
import SegmentSlider from '@/blocks/PriceCalculator/SegmentSlider'
import { PageInfo } from '@/src/@types/models'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/src/components/core/Popover'
import Container from '@/src/components/layout/Container'

export async function getStaticProps() {
  return { prices: await getPrices() }
}

const CHF_FORMAT = { maximumFractionDigits: 0 } as const

function Chf({
  value,
  className,
  strike,
}: {
  value: number
  className?: string
  strike?: boolean
}) {
  return (
    <span
      className={clsx(
        'relative inline-flex',
        strike &&
          'after:absolute after:inset-x-0 after:top-1/2 after:h-px after:-translate-y-1/2 after:bg-gray-500'
      )}
    >
      <NumberFlow
        value={value}
        locales="de-CH"
        format={CHF_FORMAT}
        prefix="CHF "
        className={className}
      />
    </span>
  )
}

export default function Component({
  prices,
  pageInfo,
}: {
  prices: PricesResponse
  pageInfo: PageInfo
}) {
  const router = useRouter()

  const [canton, setCanton] = useState<CantonCode | null>(null)
  const [hasLicense, setHasLicense] = useState(false)
  const [excludedSteps, setExcludedSteps] = useState<Set<StepId>>(new Set())

  const rootRef = useRef<HTMLDivElement>(null)
  const [sheetVisible, setSheetVisible] = useState(false)

  const [knowledge, setKnowledge] = useState(ASSESSMENT_NEUTRAL)
  const [privatePractice, setPrivatePractice] = useState(ASSESSMENT_NEUTRAL)
  const [talent, setTalent] = useState(ASSESSMENT_NEUTRAL)

  const recommendation = recommendLessons(knowledge, privatePractice, talent)
  const [lessons, setLessons] = useState(recommendation)
  const [lessonsTouched, setLessonsTouched] = useState(false)

  useEffect(() => {
    if (!lessonsTouched) setLessons(recommendation)
  }, [recommendation, lessonsTouched])

  useEffect(() => {
    const node = rootRef.current
    if (!node) return
    const observer = new IntersectionObserver(([entry]) =>
      setSheetVisible(entry.isIntersecting)
    )
    observer.observe(node)
    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    if (!router.isReady) return
    const code = router.query.kanton as string | undefined
    const canton = CANTONS.some((canton) => canton.code === code)
      ? (code as CantonCode)
      : null

    setCanton(canton)

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.isReady])

  function selectCanton(next: CantonCode | null) {
    setCanton(next)

    const params = new URLSearchParams(window.location.search)
    if (next) {
      params.set('kanton', next)
    } else {
      params.delete('kanton')
    }

    router.replace(
      {
        pathname: router.asPath.split(/[?#]/)[0],
        query: Object.fromEntries(params.entries()),
      },
      undefined,
      {
        shallow: true,
        scroll: false,
      }
    )
  }

  const cantonPrices = (canton ? prices[canton] : null) ?? null
  const steps = cantonPrices ? buildSteps(cantonPrices, lessons) : []

  const forcedExcluded = hasLicense ? LICENSE_COMPLETED_STEPS : []
  const effectiveExcluded = new Set<StepId>([
    ...excludedSteps,
    ...forcedExcluded,
  ])
  const result = computeResult(steps, effectiveExcluded)

  function toggleSelected(id: StepId, selected: boolean) {
    setExcludedSteps((previous) => {
      const next = new Set(previous)
      if (selected) next.delete(id)
      else next.add(id)
      return next
    })
  }

  return (
    <div
      ref={rootRef}
      className={clsx({ 'mt-12 md:mt-16': pageInfo.newSpacing })}
    >
      <Container>
        <div className="lg:flex lg:gap-12 xl:gap-16">
          <div
            className={clsx(
              'flex min-w-0 flex-1 flex-col gap-10 md:gap-12',
              cantonPrices && 'pb-24 lg:pb-0'
            )}
          >
            <div className="flex flex-col gap-3">
              <label>
                In welchem Kanton möchtest du den Führerschein machen?
              </label>
              <CantonCombobox value={canton} onChange={selectCanton} />
            </div>

            {cantonPrices && (
              <>
                <fieldset className="flex flex-col gap-4">
                  <legend className="mb-4">
                    Hast du bereits einen Führerschein?
                  </legend>
                  <RadioOption
                    checked={!hasLicense}
                    onChange={() => setHasLicense(false)}
                  >
                    Nein, ich habe noch keinen Führerschein.
                  </RadioOption>
                  <RadioOption
                    checked={hasLicense}
                    onChange={() => setHasLicense(true)}
                  >
                    Ja, ich habe bereits einen Führerschein (Motorrad Kat. A, -A
                    oder A1)
                  </RadioOption>
                </fieldset>

                <div className="flex flex-col gap-6">
                  <p>Wähle ab, was du bereits abgeschlossen hast.</p>
                  <div className="mt-6 flex flex-col gap-8">
                    {steps.map((step) => (
                      <StepBlock
                        key={step.id}
                        step={step}
                        selected={!effectiveExcluded.has(step.id)}
                        disabled={forcedExcluded.includes(step.id)}
                        onToggle={(selected) =>
                          toggleSelected(step.id, selected)
                        }
                        lessonSlider={
                          step.custom === 'lessons' ? (
                            <LessonEstimator
                              prices={cantonPrices}
                              lessons={lessons}
                              recommendation={recommendation}
                              knowledge={knowledge}
                              privatePractice={privatePractice}
                              talent={talent}
                              onKnowledge={(value) => {
                                setKnowledge(value)
                                setLessonsTouched(false)
                              }}
                              onPrivatePractice={(value) => {
                                setPrivatePractice(value)
                                setLessonsTouched(false)
                              }}
                              onTalent={(value) => {
                                setTalent(value)
                                setLessonsTouched(false)
                              }}
                              onLessons={(value) => {
                                setLessonsTouched(true)
                                setLessons(value)
                              }}
                            />
                          ) : null
                        }
                      />
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>

          <div className="mt-10 hidden lg:mt-0 lg:block lg:w-108 lg:shrink-0">
            <div className="lg:sticky lg:top-24">
              <ResultCard result={result} />
            </div>
          </div>
        </div>
      </Container>
      {cantonPrices && <ResultSheet result={result} visible={sheetVisible} />}
    </div>
  )
}

function RadioOption({
  checked,
  onChange,
  children,
}: {
  checked: boolean
  onChange: () => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={checked}
      onClick={onChange}
      className="flex items-start gap-3 text-left"
    >
      <span className="mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full border-2 border-blue-700">
        {checked && <span className="size-2.5 rounded-full bg-blue-700" />}
      </span>
      <span>{children}</span>
    </button>
  )
}

function StepBlock({
  step,
  selected,
  disabled,
  onToggle,
  lessonSlider,
}: {
  step: Step
  selected: boolean
  disabled: boolean
  onToggle: (selected: boolean) => void
  lessonSlider: React.ReactNode
}) {
  return (
    <div>
      <div className="mb-3 flex items-center gap-4">
        <span className="text-sm font-bold text-gray-600">
          Schritt {step.index}
        </span>
        <span className="h-px flex-1 bg-gray-300" />
      </div>
      <div className="flex items-center gap-4">
        <StepCheckbox
          checked={selected}
          disabled={disabled}
          onToggle={onToggle}
          title={step.title}
        />
        <Chf
          value={step.total}
          strike={!selected}
          className={clsx(
            'text-lg font-bold md:text-xl',
            !selected && 'text-gray-500'
          )}
        />
      </div>

      <div>
        {step.custom === 'lessons' && (
          <div className="mt-6">{lessonSlider}</div>
        )}
        {!step.custom && !step.hideRows && (
          <div className="mt-4 flex flex-col gap-2">
            {step.rows.map((row) => (
              <SubRow key={row.label} row={row} />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

function StepCheckbox({
  checked,
  disabled,
  onToggle,
  title,
}: {
  checked: boolean
  disabled: boolean
  onToggle: (checked: boolean) => void
  title: string
}) {
  return (
    <CheckboxPrimitive.Root
      checked={checked}
      disabled={disabled}
      onCheckedChange={(value) => onToggle(value === true)}
      className="group flex flex-1 items-center gap-3 text-left disabled:cursor-not-allowed"
    >
      <span className="box-border flex size-5 shrink-0 items-center justify-center rounded-xs border-2 border-blue-700 transition-colors duration-150 group-data-[state=checked]:bg-blue-700">
        <CheckboxPrimitive.Indicator forceMount>
          <svg
            width="10"
            height="9"
            viewBox="0 0 10 9"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M9.63388 1.11612C10.122 1.60427 10.122 2.39573 9.63388 2.88388L4.63388 7.88388C4.14573 8.37204 3.35427 8.37204 2.86612 7.88388L0.366117 5.38388C-0.122039 4.89573 -0.122039 4.10427 0.366117 3.61612C0.854272 3.12796 1.64573 3.12796 2.13388 3.61612L3.75 5.23223L7.86612 1.11612C8.35427 0.627961 9.14573 0.627961 9.63388 1.11612Z"
              fill="#FFF"
            />
          </svg>
        </CheckboxPrimitive.Indicator>
      </span>
      <span className="text-lg font-black md:text-xl">{title}</span>
    </CheckboxPrimitive.Root>
  )
}

function SubRow({ row }: { row: Row }) {
  return (
    <div className="flex items-center justify-between gap-4 text-gray-800">
      <span className="flex items-center gap-1.5">
        {row.label}
        {row.info && (
          <Popover>
            <PopoverTrigger className="inline-flex">
              <InfoIcon size={20} weight="fill" className="text-gray-800" />
            </PopoverTrigger>
            <PopoverPortal>
              <PopoverContent>
                <p>{row.info}</p>
              </PopoverContent>
            </PopoverPortal>
          </Popover>
        )}
      </span>
      <span className="shrink-0">{row.note ?? formatChf(row.amount)}</span>
    </div>
  )
}

function LessonEstimator({
  prices,
  lessons,
  recommendation,
  knowledge,
  privatePractice,
  talent,
  onKnowledge,
  onPrivatePractice,
  onTalent,
  onLessons,
}: {
  prices: CantonPrices
  lessons: number
  recommendation: number
  knowledge: number
  privatePractice: number
  talent: number
  onKnowledge: (value: number) => void
  onPrivatePractice: (value: number) => void
  onTalent: (value: number) => void
  onLessons: (value: number) => void
}) {
  const packing = packLessons(lessons, prices)
  const breakdown = lessonBreakdownRows(packing, prices)

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-3 rounded-xl bg-linear-to-b from-gray-50 to-gray-100 p-5">
        <p className="text-sm text-gray-800">Schätze dich selber ein:</p>
        <div className="flex flex-col gap-5">
          <SegmentSlider
            title="Vorkenntnisse"
            description="Wie viel weisst und kannst du bereits?"
            value={knowledge}
            onChange={onKnowledge}
            minLabel="Gar nichts"
            maxLabel="Ich kann bereits Fahren"
          />
          <SegmentSlider
            title="Privat Lernfahren"
            description="Wie oft kannst du auch privat üben?"
            value={privatePractice}
            onChange={onPrivatePractice}
            minLabel="Nie"
            maxLabel="Mehrmals pro Woche"
          />
          <SegmentSlider
            title="Talent"
            description="Wie liegt dir das Autofahren?"
            value={talent}
            onChange={onTalent}
            minLabel="Kein Talent"
            maxLabel="Naturtalent"
          />
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <p className="text-gray-800">Anzahl Fahrlektionen</p>
        <RangeSlider
          value={lessons}
          min={LESSON_SLIDER_MIN}
          max={LESSON_SLIDER_MAX}
          marker={recommendation}
          onChange={onLessons}
          minLabel="0"
          maxLabel="50"
          ariaLabel="Anzahl Fahrlektionen"
        />
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <span className="size-3 rounded-full bg-yellow-600" />
          Unsere Einschätzung: {recommendation} Fahrlektionen
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <div className="flex items-center justify-between gap-4">
          <span className="text-lg font-bold">
            <NumberFlow value={lessons} /> Fahrlektionen à 45 Minuten
          </span>
          <Chf value={packing.cost} className="shrink-0" />
        </div>
        {breakdown.map((row) => (
          <div
            key={row.id}
            className="flex items-center justify-between gap-4 text-gray-800"
          >
            <span>
              <NumberFlow value={row.count} suffix="x" /> {row.label}
            </span>
            <Chf value={row.amount} className="shrink-0" />
          </div>
        ))}
        <div className="flex items-center justify-between gap-4 text-gray-800">
          <span>Adminpauschale bei BLINK</span>
          <Chf value={ADMIN_FEE} className="shrink-0" />
        </div>
      </div>
      <div className="flex max-w-140 items-center gap-3 rounded-lg bg-linear-to-b from-yellow-50 to-yellow-100 p-4 text-sm text-yellow-950">
        <ShootingStarIcon
          size={20}
          weight="bold"
          className="shrink-0 text-yellow-950"
        />
        <p>
          Fahrschüler:innen, welche die Fahrausbildung mit Automatik-Getriebe
          machen brauchen <b>durchschnittlich 17 Fahrlektionen à 45 Minuten.</b>
        </p>
      </div>
    </div>
  )
}

function ResultBody({ result }: { result: ReturnType<typeof computeResult> }) {
  return (
    <>
      <div className="flex flex-col divide-y divide-gray-200">
        {RESULT_ROWS.map((row) => (
          <div
            key={row.category}
            className="flex items-center justify-between gap-4 py-3 lg:font-bold"
          >
            <span>{row.label}</span>
            <Chf value={result[row.category]} className="shrink-0" />
          </div>
        ))}
      </div>
      <div className="border-t border-gray-200 pt-4">
        <div className="flex items-center justify-between gap-4">
          <span className="text-lg font-bold">Total</span>
          <Chf value={result.total} className="text-xl font-black" />
        </div>
        <p className="mt-3 text-sm text-gray-600">
          Der Gesamtbetrag basiert auf durchschnittlichen Preisen und deinen
          Angaben. Die effektiven Kosten können je nach Fahrschule,
          Lernfortschritt und Kanton unterschiedlich ausfallen.
        </p>
      </div>
    </>
  )
}

function ResultCard({ result }: { result: ReturnType<typeof computeResult> }) {
  return (
    <div className="rounded-2xl border border-gray-200 bg-gray-50 p-6">
      <h3 className="text-xl font-black">Dein Ergebnis</h3>
      <ResultBody result={result} />
    </div>
  )
}

function ResultSheet({
  result,
  visible,
}: {
  result: ReturnType<typeof computeResult>
  visible: boolean
}) {
  const [expanded, setExpanded] = useState(false)

  useEffect(() => {
    if (!visible) setExpanded(false)
  }, [visible])

  return (
    <div className="lg:hidden">
      <div
        className={clsx(
          'fixed inset-x-0 bottom-0 z-50 border-t border-gray-200 bg-linear-to-b from-gray-50 to-gray-100 shadow-lg transition-transform duration-300',
          expanded && 'pb-8',
          !visible && 'translate-y-full'
        )}
      >
        <button
          type="button"
          onClick={() => setExpanded((value) => !value)}
          className={clsx(
            'flex w-full flex-col gap-3 px-6 pt-3',
            expanded ? 'pb-2' : 'pb-6'
          )}
        >
          <span className="mx-auto h-1 w-12 rounded-full bg-gray-300" />
          <span className="flex items-baseline justify-between gap-4">
            <span className="text-lg font-black">Dein Ergebnis</span>
            {!expanded && (
              <Chf value={result.total} className="text-lg font-black" />
            )}
          </span>
        </button>
        <div
          className="grid transition-all duration-300"
          style={{ gridTemplateRows: expanded ? '1fr' : '0fr' }}
        >
          <div className="overflow-hidden px-6">
            <ResultBody result={result} />
          </div>
        </div>
      </div>
    </div>
  )
}
